/**
 * consentWording.ts — the marketing-consent WORDING, and the version id it is
 * stored under. ONE constant, read by every surface that shows it.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: marketing consent
 * (2026-09-11, CTO: capture consent to contact by EMAIL and by WHATSAPP after
 * the initial qualification, in separate, unticked, specific boxes).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ✅ APPROVED FOR PRODUCTION — Samitha Heanthenna (CTO), 2026-09-14.
 *
 * Reviewed as the words a patient actually reads on the Result screen and on
 * `/preferences`, in the two-box, unticked form below. This is the approval the
 * block that used to stand here was waiting for, and it is what releases the
 * wording to `main`.
 *
 * (That was the placement on 2026-09-14. On 2026-09-23 — also the CTO's call —
 * the patient's first sight of these words moved from the Result screen to the
 * questionnaire's consent step, in its own block outside "Select all" (since
 * 2026-09-24 that control is the ONE "I agree to all of the above" tick under
 * the declarations, and this block sits after it — the tick never reaches it
 * either). Not one string changed, so the version id did not either. The
 * WhatsApp line promises
 * "the mobile number I gave you", so that block shows it only when its draft
 * read reports a UK mobile, and the email box alone while that is unknown —
 * see `journeyConsent.ts`.)
 *
 * ⚠️ NO SEPARATE DPO SIGN-OFF IS ON RECORD. Stated because UK GDPR Art 7(1)
 * puts the burden of proving consent on us, and an audit trail that names the
 * wrong approver is worse than one that names none: this records exactly who
 * approved it and in what capacity. If a Data Protection Officer is appointed,
 * their review belongs beside this line rather than replacing it.
 *
 * ⚠️ THE VERSION ID DOES NOT CHANGE, and the `-d1` suffix now reads oddly. It
 * stays anyway. The id names the WORDS, not their approval state, and not one
 * character of the strings below moved — so every grant already stored against
 * `mc-2026-09-11-d1` still describes exactly what its patient saw. Reissuing it
 * as `-v1` would make clinic-service's registry answer 400 until it learned the
 * new id, and would fork one set of words across two ids for no gain. A NEW id
 * is for a REWORD; see the paragraph below.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * WHY ONE CONSTANT AND NOT A LITERAL PER SCREEN. UK GDPR Art 7(1) puts the
 * burden of proving consent on us, and what we can prove is "this person ticked
 * the box whose words were version X". clinic-service stores the version id with
 * every grant and holds the words for each id in its own registry, so the id
 * this portal sends has to name the words this portal SHOWED. Two screens show
 * them — the questionnaire's CONSENT SCREEN (the "Keeping in touch" block under
 * the required declarations; it moved there from the eligible Result screen on
 * 2026-09-23, CTO — the words themselves are UNCHANGED, so the version id did
 * not move) and the public `/preferences` page — and if each kept its own copy,
 * one edit would make the stored version describe words the patient never saw.
 * So both render from here, and a change to ANY string below is a new version
 * id, never an edit in place: clinic-service's registry must learn the new id
 * first, or its POST answers 400.
 *
 * VERBATIM. The strings are the contract's, character for character (straight
 * apostrophes included). Do not "tidy" the typography: a curly apostrophe is a
 * different string from the one the registry holds against this id.
 *
 * PECR reg. 22 + UK GDPR Art 7: separate boxes (one per channel), specific,
 * UNTICKED by default, and as easy to withdraw as to give — which is what the
 * withdraw line promises and what `/preferences` and the email footer deliver.
 */

/** The version id clinic-service stores against every grant made with these words. */
export const MARKETING_CONSENT_WORDING_VERSION = 'mc-2026-09-11-d1';

/** The words themselves. Rendered verbatim; see the header before changing any of them. */
export const MARKETING_CONSENT_WORDING = {
  version: MARKETING_CONSENT_WORDING_VERSION,
  heading: 'Keeping in touch',
  intro:
    "Would you like The GP Service to keep in touch about your check and our GPS Clinics services? It's optional, and saying no won't affect your check or your care.",
  email: 'Email me reminders about my check and news about GPS Clinics services.',
  whatsapp: 'Send me WhatsApp messages about my check and GPS Clinics services, on the mobile number I gave you.',
  withdraw:
    'You can change your mind at any time: use the link at the bottom of any of our emails, reply STOP to a WhatsApp message, or ask a member of our team.',
} as const;
